// T3: Vendor / staff access portal.
//
// Covers Stitch v2 design `vendor_staff_access_control`. Master + tenant_admin
// only (capability TENANT_MANAGE_VENDORS — sensitive: physical access grants
// to third parties). Status (active / expired / pending / revoked) is computed
// client-side from the stored valid_from / valid_until / revoked_at.

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Badge, Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import {
  type GrantVendorAccessResponse,
  type VendorAccess,
  type VendorAccessStatus,
  type VendorAccessSyncStatus,
  vendorAccessStatus,
} from '@ng-unifi/domain';
import { apiClient, ApiError, newIdempotencyKey } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { useTenantScope } from '../auth/TenantScopeContext.js';

interface AccessGroupOption {
  id: string;
  name: string;
}

interface TenantOption {
  id: string;
  name: string;
}

type VendorAccessRow = VendorAccess & {
  tenant?: { id: string; name: string; status: string } | null;
  access_group?: AccessGroupOption | null;
};

const STATUS_TONE: Record<VendorAccessStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  active: 'success',
  pending: 'warning',
  expired: 'danger',
  revoked: 'neutral',
};

const STATUS_LABEL: Record<VendorAccessStatus, string> = {
  active: 'Active',
  pending: 'Pending',
  expired: 'Expired',
  revoked: 'Revoked',
};

const SYNC_TONE: Record<VendorAccessSyncStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  synced: 'success',
  pending: 'warning',
  retry: 'warning',
  failed: 'danger',
};

const SYNC_LABEL: Record<VendorAccessSyncStatus, string> = {
  synced: 'Controller synced',
  pending: 'Syncing…',
  retry: 'Retrying…',
  failed: 'Sync failed',
};

function toDatetimeLocal(d: Date): string {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function VendorAccessPage() {
  const { state } = useAuth();
  const { tenantId } = useTenantScope();
  const [rows, setRows] = useState<VendorAccessRow[] | null>(null);
  const [groups, setGroups] = useState<AccessGroupOption[]>([]);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(tenantId ?? '');
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<VendorAccess | null>(null);
  // F2: PIN shown ONCE after a successful grant. The grant route returns
  // the full PIN in its response; we surface it in a copy-once modal and
  // never persist it client-side. After dismissal it cannot be retrieved.
  const [grantedPin, setGrantedPin] = useState<{ name: string; pin: string } | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  const isAllowed =
    state.status === 'authed' &&
    (state.user.role === 'master_admin' || state.user.role === 'tenant_admin');
  const buildingView = state.status === 'authed' && state.user.role === 'master_admin' && !tenantId;

  useEffect(() => {
    if (tenantId) setSelectedTenantId(tenantId);
  }, [tenantId]);

  useEffect(() => {
    if (!isAllowed) return;
    let cancelled = false;
    setError(null);
    const tenantForGroups = buildingView ? selectedTenantId : tenantId;
    Promise.all([
      buildingView
        ? apiClient.get<{ data: VendorAccessRow[] }>('/api/v1/admin/vendors', {
            skipTenantScope: true,
          })
        : apiClient.get<{ data: VendorAccessRow[] }>('/api/v1/vendors'),
      tenantForGroups
        ? (buildingView
            ? apiClient.get<{ data: AccessGroupOption[] }>(
                `/api/v1/admin/vendors/access-groups?tenant_id=${tenantForGroups}`,
                { skipTenantScope: true },
              )
            : apiClient.get<{ data: AccessGroupOption[] }>('/api/v1/access-groups'))
        : Promise.resolve({ data: [] as AccessGroupOption[] }),
      buildingView
        ? apiClient
            .get<{ data: TenantOption[] }>('/api/v1/tenants', { skipTenantScope: true })
            .catch(() => ({ data: [] as TenantOption[] }))
        : Promise.resolve({ data: [] as TenantOption[] }),
    ])
      .then(([v, g, t]) => {
        if (cancelled) return;
        setRows(v.data);
        setGroups(g.data);
        setTenants(t.data);
        if (buildingView && !selectedTenantId && t.data[0]) {
          setSelectedTenantId(t.data[0].id);
        }
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof ApiError ? e.message : 'Failed to load');
          setRows([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [buildingView, isAllowed, reloadTick, selectedTenantId, tenantId]);

  if (!isAllowed) {
    return (
      <p role="alert" className="text-error">
        <Icon name="lock" /> Vendor access management requires tenant-admin or master role.
      </p>
    );
  }

  if (rows === null) {
    return (
      <div className="p-12 flex justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Vendor &amp; staff access</h1>
          <p className="text-on-surface-variant">
            Time-bound third-party physical access grants. Revoke at any time;
            expiry shows automatically.
          </p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          disabled={buildingView ? !selectedTenantId || groups.length === 0 : groups.length === 0}
        >
          <Icon name="add" /> Grant access
        </Button>
      </header>

      {buildingView ? (
        <Card>
          <CardBody>
            <label className="grid gap-1 max-w-md">
              <span className="font-label-md text-label-md text-on-surface-variant">
                Tenant for new grants
              </span>
              <select
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                className="rounded border border-outline bg-surface px-3 py-2"
              >
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </label>
          </CardBody>
        </Card>
      ) : null}

      {error ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {error}
        </p>
      ) : null}

      <Card>
        <CardBody>
          {rows.length === 0 ? (
            <p className="py-3 text-on-surface-variant">No vendor grants yet.</p>
          ) : (
            <ul className="divide-y divide-outline-variant">
              {rows.map((r) => {
                const status = vendorAccessStatus(r);
                const group = r.access_group ?? groups.find((g) => g.id === r.access_group_id);
                return (
                  <li key={r.id} className="flex items-center gap-3 py-3">
                    {/* `status` is a typed VendorAccessStatus union, not user input. */}
                    {/* eslint-disable-next-line security/detect-object-injection */}
                    <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{r.name}</p>
                      <p className="text-sm text-on-surface-variant">
                        {buildingView && r.tenant ? `${r.tenant.name} · ` : ''}
                        {group?.name ?? 'unknown group'} ·{' '}
                        {new Date(r.valid_from).toLocaleDateString()} →{' '}
                        {new Date(r.valid_until).toLocaleDateString()}
                        {r.pin_code_last4 ? <> · PIN ··{r.pin_code_last4}</> : null}
                      </p>
                      {r.purpose ? (
                        <p className="text-sm text-on-surface-variant">{r.purpose}</p>
                      ) : null}
                      {r.sync_status !== 'synced' ? (
                        <p className="text-xs text-on-surface-variant mt-1">
                          <Badge tone={SYNC_TONE[r.sync_status]}>
                            {SYNC_LABEL[r.sync_status]}
                          </Badge>
                          {r.last_sync_error ? (
                            <span className="ml-2 text-error">{r.last_sync_error}</span>
                          ) : null}
                        </p>
                      ) : null}
                    </div>
                    {status !== 'revoked' ? (
                      <Button variant="ghost" onClick={() => setRevokeTarget(r)}>
                        Revoke
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      {createOpen ? (
        <CreateModal
          groups={groups}
          tenantId={buildingView ? selectedTenantId : null}
          onCancel={() => setCreateOpen(false)}
          onCreated={(granted) => {
            setCreateOpen(false);
            // F2: if the grant response carries a PIN (sync controller
            // path succeeded), surface it in the one-time PIN modal.
            // If not (failed → outbox retry), the operator sees the
            // row's sync_status badge instead.
            if (granted.pin_code) {
              setGrantedPin({ name: granted.name, pin: granted.pin_code });
            }
            reload();
          }}
        />
      ) : null}

      {grantedPin ? (
        <PinShownOnceModal
          name={grantedPin.name}
          pin={grantedPin.pin}
          onClose={() => setGrantedPin(null)}
        />
      ) : null}

      {revokeTarget ? (
        <RevokeModal
          row={revokeTarget}
          tenantId={buildingView ? revokeTarget.tenant_id : null}
          onCancel={() => setRevokeTarget(null)}
          onRevoked={() => {
            setRevokeTarget(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateModal({
  groups,
  tenantId,
  onCancel,
  onCreated,
}: {
  groups: AccessGroupOption[];
  tenantId: string | null;
  onCancel: () => void;
  onCreated: (granted: GrantVendorAccessResponse) => void;
}) {
  const now = new Date();
  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [purpose, setPurpose] = useState('');
  const [validFrom, setValidFrom] = useState(toDatetimeLocal(now));
  const [validUntil, setValidUntil] = useState(toDatetimeLocal(tomorrow));
  const [groupId, setGroupId] = useState(groups[0]?.id ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idemKey] = useState(() => newIdempotencyKey());

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiClient
        .withIdempotencyKey(idemKey)
        .post<{ data: GrantVendorAccessResponse }>(
          '/api/v1/vendors',
          {
            name: name.trim(),
            phone: phone.trim() || undefined,
            purpose: purpose.trim() || undefined,
            valid_from: new Date(validFrom).toISOString(),
            valid_until: new Date(validUntil).toISOString(),
            access_group_id: groupId,
          },
          tenantId ? { skipTenantScope: true, headers: { 'x-tenant-context': tenantId } } : undefined,
        );
      onCreated(res.data);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to grant access');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="grant-vendor-title"
      className="fixed inset-0 bg-on-surface/40 flex items-center justify-center p-4 z-50"
    >
      <Card className="w-full max-w-md">
        <CardBody>
          <h2 id="grant-vendor-title" className="text-lg font-semibold mb-3">
            Grant vendor access
          </h2>
          <form onSubmit={onSubmit} className="grid gap-3">
            <label className="grid gap-1">
              <span>Vendor name</span>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded border border-outline px-3 py-2"
              />
            </label>
            <label className="grid gap-1">
              <span>Phone (optional)</span>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="rounded border border-outline px-3 py-2"
              />
            </label>
            <label className="grid gap-1">
              <span>Purpose (optional)</span>
              <input
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                maxLength={500}
                className="rounded border border-outline px-3 py-2"
              />
            </label>
            <label className="grid gap-1">
              <span>Access group</span>
              <select
                required
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="rounded border border-outline px-3 py-2"
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <label className="grid gap-1">
                <span>Valid from</span>
                <input
                  type="datetime-local"
                  required
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  className="rounded border border-outline px-3 py-2"
                />
              </label>
              <label className="grid gap-1">
                <span>Valid until</span>
                <input
                  type="datetime-local"
                  required
                  value={validUntil}
                  onChange={(e) => setValidUntil(e.target.value)}
                  className="rounded border border-outline px-3 py-2"
                />
              </label>
            </div>
            {error ? (
              <p role="alert" className="text-error">
                <Icon name="error" /> {error}
              </p>
            ) : null}
            <div className="flex gap-2 justify-end mt-2">
              <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting || !groupId}>
                {submitting ? 'Granting…' : 'Grant'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function RevokeModal({
  row,
  tenantId,
  onCancel,
  onRevoked,
}: {
  row: VendorAccess;
  tenantId: string | null;
  onCancel: () => void;
  onRevoked: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(
        `/api/v1/vendors/${row.id}/revoke`,
        { reason: reason.trim() },
        tenantId ? { skipTenantScope: true, headers: { 'x-tenant-context': tenantId } } : undefined,
      );
      onRevoked();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to revoke');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="revoke-vendor-title"
      className="fixed inset-0 bg-on-surface/40 flex items-center justify-center p-4 z-50"
    >
      <Card className="w-full max-w-md">
        <CardBody>
          <h2 id="revoke-vendor-title" className="text-lg font-semibold mb-3">
            Revoke access for {row.name}
          </h2>
          <form onSubmit={onSubmit} className="grid gap-3">
            <label className="grid gap-1">
              <span>Reason (required, recorded in audit)</span>
              <input
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                className="rounded border border-outline px-3 py-2"
              />
            </label>
            {error ? (
              <p role="alert" className="text-error">
                <Icon name="error" /> {error}
              </p>
            ) : null}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="danger" disabled={submitting || !reason.trim()}>
                {submitting ? 'Revoking…' : 'Revoke'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

/**
 * One-time PIN display. Renders after a successful grant. The full PIN
 * lives only here — closing the modal makes it irretrievable from the
 * portal (the controller-generated PIN was returned ONCE by
 * createCredential and the portal only persists the last 4 digits).
 */
function PinShownOnceModal({
  name,
  pin,
  onClose,
}: {
  name: string;
  pin: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(pin);
      setCopied(true);
    } catch {
      // Clipboard write can be blocked in headless / older browsers;
      // operator can read the PIN visually as a fallback.
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pin-shown-once-title"
      className="fixed inset-0 bg-on-surface/40 flex items-center justify-center p-4 z-50"
    >
      <Card className="w-full max-w-md">
        <CardBody>
          <h2 id="pin-shown-once-title" className="text-lg font-semibold mb-2">
            PIN issued for {name}
          </h2>
          <p className="text-sm text-on-surface-variant mb-3">
            This PIN is shown <strong>only once</strong>. Copy it now and hand
            it to the vendor — the portal stores only the last 4 digits for
            later identification.
          </p>
          <div className="bg-surface-container-high rounded p-4 text-center font-mono text-3xl tracking-widest mb-3 select-all">
            {pin}
          </div>
          {copied ? (
            <p className="text-success text-sm mb-2">
              <Icon name="check_circle" /> Copied to clipboard
            </p>
          ) : null}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="ghost" onClick={copy}>
              <Icon name="content_copy" /> Copy PIN
            </Button>
            <Button type="button" variant="danger" onClick={onClose}>
              I've saved it — dismiss
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
